/**
 * The redemption-code pool for one distribution: upload codes, hand them out, and see at
 * a glance which were used.
 *
 * Codes are shown in FULL — they are not secrets, and each row carries its one-tap redeem
 * link (`https://apps.apple.com/redeem?ctx=apps&code=…`), which opens the App Store with
 * the code already entered. Nobody should ever type a code by hand: a mistyped character
 * wastes a single-use code.
 *
 * A used code is TERMINAL — single-use and spent — so it offers no actions at all. The row
 * is a record, not a control. (The server enforces this with a 409 regardless.)
 */

import { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import {
  IconUpload,
  IconUserPlus,
  IconCopy,
  IconBan,
  IconArrowBackUp,
  IconCircleCheck,
  IconAlertTriangle,
  IconTicket,
  IconExternalLink,
  IconSearch,
} from '@tabler/icons-react';
import {
  Stack,
  Card,
  Group,
  Button,
  Text,
  TextInput,
  Select,
  Badge,
  Paper,
  Table,
  SimpleGrid,
  Alert,
  Tooltip,
  ActionIcon,
  Loader,
  toast,
} from '@/components/ui';
import { ActionIcon as MantineActionIcon } from '@mantine/core';
import { useAppDistributionService, CODE_STATUS } from '@actschurch/ccms-sdk';
import type { AppCodeView, AppDistributionView } from '@actschurch/ccms-sdk';
import { buildRedeemLink } from '@/utils/appleRedeem';
import UploadCodesModal from './UploadCodesModal';
import AssignCodesModal from './AssignCodesModal';

const PAGE_SIZE = 25;

interface AppCodePoolProps {
  distribution: AppDistributionView;
  /** e.g. "CCMS Tasks — iOS" */
  targetLabel: string;
  onChanged: () => void;
}

export default function AppCodePool({ distribution, targetLabel, onChanged }: AppCodePoolProps) {
  const queryClient = useQueryClient();
  const distributionService = useAppDistributionService();

  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);

  const distributionId = distribution.distributionId;

  const { data: codesResponse, isLoading } = useQuery({
    queryKey: ['admin', 'app-catalogue', 'codes', distributionId, statusFilter, search, page],
    queryFn: () =>
      distributionService.getCodesRaw(distributionId, {
        statusId: statusFilter ? Number(statusFilter) : undefined,
        search: search.trim() || undefined,
        skip: page * PAGE_SIZE,
        take: PAGE_SIZE,
      }),
  });

  const codes = codesResponse?.data ?? [];
  const total = codesResponse?.totalCount ?? 0;
  const stats = distribution.stats;

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'app-catalogue', 'codes'] });
    onChanged();
  };

  return (
    <Card withBorder padding="md" radius="md">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <Group gap="xs">
            <IconTicket size={18} />
            <Text fw={600}>Codes</Text>
          </Group>
          <Group gap="xs">
            <Button
              variant="light"
              size="xs"
              leftSection={<IconUpload size={16} />}
              onClick={() => setUploadOpen(true)}
            >
              Upload codes
            </Button>
            <Button
              size="xs"
              leftSection={<IconUserPlus size={16} />}
              onClick={() => setAssignOpen(true)}
            >
              Assign a code
            </Button>
          </Group>
        </Group>

        {stats && (
          <>
            <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
              <Tile
                label="Available"
                value={stats.available}
                color={stats.isLowStock ? 'red' : 'green'}
              />
              <Tile label="Assigned" value={stats.assigned} color="blue" />
              <Tile label="Used" value={stats.redeemed} color="violet" />
              <Tile label="Total" value={stats.total} color="gray" />
            </SimpleGrid>

            {stats.isLowStock && (
              <Alert color="red" icon={<IconAlertTriangle size={16} />}>
                <Text size="sm">
                  Only {stats.available} code(s) left to hand out. Request more from Apple
                  Business Manager before someone is left without one.
                </Text>
              </Alert>
            )}
          </>
        )}

        <Group>
          <TextInput
            placeholder="Search by code, name or email…"
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => {
              setSearch(e.currentTarget.value);
              setPage(0);
            }}
            style={{ flex: 1 }}
          />
          <Select
            placeholder="All statuses"
            clearable
            value={statusFilter}
            onChange={(v) => {
              setStatusFilter(v);
              setPage(0);
            }}
            data={[
              { value: String(CODE_STATUS.AVAILABLE), label: 'Available' },
              { value: String(CODE_STATUS.ASSIGNED), label: 'Assigned' },
              { value: String(CODE_STATUS.REDEEMED), label: 'Used' },
              { value: String(CODE_STATUS.REVOKED), label: 'Revoked' },
              { value: String(CODE_STATUS.EXPIRED), label: 'Expired' },
              { value: String(CODE_STATUS.INVALID), label: 'Invalid' },
            ]}
            w={170}
          />
        </Group>

        {isLoading ? (
          <Group justify="center" py="xl">
            <Loader size="sm" />
          </Group>
        ) : codes.length === 0 ? (
          <Paper withBorder p="xl" ta="center">
            <Stack gap="sm" align="center">
              <IconTicket size={32} color="var(--mantine-color-gray-5)" />
              <Text c="dimmed">
                {total === 0 && !search && !statusFilter
                  ? 'This pool is empty. Upload the codes Apple gave you to get started.'
                  : 'No codes match your filter.'}
              </Text>
              {total === 0 && !search && !statusFilter && (
                <Button
                  leftSection={<IconUpload size={16} />}
                  onClick={() => setUploadOpen(true)}
                >
                  Upload codes
                </Button>
              )}
            </Stack>
          </Paper>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <Table verticalSpacing="sm" style={{ minWidth: 820 }}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Code</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Held by</Table.Th>
                    <Table.Th>Used</Table.Th>
                    <Table.Th ta="right">Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {codes.map((code) => (
                    <CodeRow
                      key={code.codeId}
                      code={code}
                      installUrl={distribution.installUrl}
                      onChanged={refresh}
                    />
                  ))}
                </Table.Tbody>
              </Table>
            </div>

            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
              </Text>
              <Group gap="xs">
                <Button
                  variant="default"
                  size="xs"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  Previous
                </Button>
                <Button
                  variant="default"
                  size="xs"
                  disabled={(page + 1) * PAGE_SIZE >= total}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </Group>
            </Group>
          </>
        )}
      </Stack>

      <UploadCodesModal
        opened={uploadOpen}
        onClose={() => setUploadOpen(false)}
        distributionId={distributionId}
        targetLabel={targetLabel}
        onUploaded={refresh}
      />
      <AssignCodesModal
        opened={assignOpen}
        onClose={() => setAssignOpen(false)}
        distributionId={distributionId}
        targetLabel={targetLabel}
        available={stats?.available ?? 0}
        onAssigned={refresh}
      />
    </Card>
  );
}

function CodeRow({
  code,
  installUrl,
  onChanged,
}: {
  code: AppCodeView;
  installUrl?: string | null;
  onChanged: () => void;
}) {
  const distributionService = useAppDistributionService();

  // Same fallback as the user's page: the API sends redeemUrl per code, but we can build
  // it from the code itself, so the link works even against an older API.
  const redeemUrl = code.redeemUrl ?? buildRedeemLink(code.codeValue, installUrl);

  const isRedeemed = code.statusId === CODE_STATUS.REDEEMED;
  const isAvailable = code.statusId === CODE_STATUS.AVAILABLE;
  const isAssigned = code.statusId === CODE_STATUS.ASSIGNED;

  const mutate = (fn: () => Promise<boolean>, successMessage: string) => ({
    mutationFn: fn,
    onSuccess: () => {
      toast.success(successMessage);
      onChanged();
    },
    onError: (err: any) => {
      const message: string = err?.message ?? '';
      // The lifecycle is guarded in SQL. If the server says no, the code is already spent —
      // say that, rather than "something went wrong".
      if (/409|already been used|revoked|expired/i.test(message)) {
        toast.error('That code is already spent', 'A used code cannot be changed.');
        onChanged();
      } else {
        toast.error('Action failed', message);
      }
    },
  });

  const unassign = useMutation(
    mutate(() => distributionService.unassignCode(code.codeId), 'Code returned to the pool')
  );
  const revoke = useMutation(
    mutate(() => distributionService.revokeCode(code.codeId), 'Code revoked')
  );
  const markUsed = useMutation(
    mutate(() => distributionService.markCodeRedeemed(code.codeId), 'Code marked as used')
  );

  const busy = unassign.isPending || revoke.isPending || markUsed.isPending;

  const copy = async (value: string, what: string) => {
    await navigator.clipboard.writeText(value);
    toast.success(`${what} copied`);
  };

  return (
    <Table.Tr>
      <Table.Td>
        <Group gap={6} wrap="nowrap">
          <Text ff="monospace" size="sm" fw={600}>
            {code.codeValue}
          </Text>
          <Tooltip label="Copy the code">
            <ActionIcon
              variant="subtle"
              size="sm"
              color="gray"
              onClick={() => copy(code.codeValue, 'Code')}
            >
              <IconCopy size={14} />
            </ActionIcon>
          </Tooltip>
          {/* The download link is the thing you actually want to send someone: it opens the
              App Store with this code already filled in. One per code. */}
          {redeemUrl && (
            <>
              <Tooltip label="Copy the download link (opens the App Store with this code)">
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  color="violet"
                  onClick={() => copy(redeemUrl, 'Download link')}
                >
                  <IconTicket size={14} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Open the download link">
                <MantineActionIcon
                  variant="subtle"
                  size="sm"
                  color="gray"
                  component="a"
                  href={redeemUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open the download link"
                >
                  <IconExternalLink size={14} />
                </MantineActionIcon>
              </Tooltip>
            </>
          )}
        </Group>
      </Table.Td>

      <Table.Td>
        <Badge color={statusColor(code.statusId)} variant="light">
          {isRedeemed ? 'Used' : code.statusName}
        </Badge>
      </Table.Td>

      <Table.Td>
        {code.assignedUserId ? (
          <div>
            <Text size="sm">{code.assignedUserName || '(no name)'}</Text>
            <Text size="xs" c="dimmed">
              {code.assignedUserEmail || code.assignedUserId}
            </Text>
          </div>
        ) : (
          <Text size="sm" c="dimmed">
            —
          </Text>
        )}
      </Table.Td>

      <Table.Td>
        {isRedeemed ? (
          <Group gap={4} wrap="nowrap">
            <IconCircleCheck size={14} color="var(--mantine-color-violet-6)" />
            <Text size="xs" c="dimmed">
              {code.redeemedAt ? new Date(code.redeemedAt).toLocaleDateString() : 'Yes'}
            </Text>
          </Group>
        ) : (
          <Text size="xs" c="dimmed">
            Not yet
          </Text>
        )}
      </Table.Td>

      <Table.Td>
        <Group gap={4} justify="flex-end" wrap="nowrap">
          {/* A used code is spent: no unassign, no revoke, no reset. */}
          {isAssigned && (
            <>
              <Tooltip label="They told me they used it">
                <ActionIcon
                  variant="subtle"
                  color="violet"
                  size="sm"
                  loading={markUsed.isPending}
                  disabled={busy}
                  onClick={() => markUsed.mutate()}
                >
                  <IconCircleCheck size={16} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Return to the pool">
                <ActionIcon
                  variant="subtle"
                  color="yellow"
                  size="sm"
                  loading={unassign.isPending}
                  disabled={busy}
                  onClick={() => unassign.mutate()}
                >
                  <IconArrowBackUp size={16} />
                </ActionIcon>
              </Tooltip>
            </>
          )}

          {(isAvailable || isAssigned) && (
            <Tooltip label="Burn this code so nobody can use it">
              <ActionIcon
                variant="subtle"
                color="red"
                size="sm"
                loading={revoke.isPending}
                disabled={busy}
                onClick={() => revoke.mutate()}
              >
                <IconBan size={16} />
              </ActionIcon>
            </Tooltip>
          )}

          {code.isTerminal && (
            <Text size="xs" c="dimmed">
              {isRedeemed ? 'Spent' : 'Closed'}
            </Text>
          )}
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}

function Tile({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <Paper withBorder p="sm" radius="md">
      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
        {label}
      </Text>
      <Text size="xl" fw={700} c={color}>
        {value}
      </Text>
    </Paper>
  );
}

function statusColor(statusId: number): string {
  switch (statusId) {
    case CODE_STATUS.AVAILABLE:
      return 'green';
    case CODE_STATUS.ASSIGNED:
      return 'blue';
    case CODE_STATUS.REDEEMED:
      return 'violet';
    case CODE_STATUS.REVOKED:
      return 'red';
    case CODE_STATUS.EXPIRED:
      return 'gray';
    default:
      return 'orange';
  }
}
